import React, { useCallback, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';

import { callTokenApi } from '../../../../../../../apiCaller';
import { API_AI_INSIGHTS_INTENT_DATASET_BY_ID, API_AI_INSIGHTS_INTENT_DATASET_RULE, COMPONENT_NAME } from '../../../../../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { AIIntentsDetailContext } from '../DateSet';
import StepCardHeader from '../../../../analytics/ai-insights/Components/StepCardHeaders';

import CIcon from '@coreui/icons-react';
import { CLink } from '@coreui/react';
import { HoverInfoIconTooltip } from '../../../../../../general/custom-tooltips';
import { setFlexibleMultiModal } from '../../../../../../../actions/common';

const BuildEditDataSet = () => {
     const { activeStep, setActiveStep, stepsData, setStepsData, setViewDatasets, handleHasChange } = useContext(AIIntentsDetailContext);
     const dispatch = useDispatch();
     const activeAccount = useSelector((state) => state.subscriber.activeAccount);

     const { id: datasetId, lastEditStep, name, description, oldData } = stepsData;

     const onSubmit = useCallback(
          async (stepsData) => {
               const currentStep = 2;
               const payload = {
                    accountId: activeAccount.accountId,
                    data: JSON.stringify(stepsData.data || []),
                    datasetId: stepsData.id,
               };

               try {
                    const isUpdate = !!oldData;
                    const method = isUpdate ? 'PUT' : 'POST';
                    const endpoint = isUpdate ? `${API_AI_INSIGHTS_INTENT_DATASET_RULE}/${datasetId}` : API_AI_INSIGHTS_INTENT_DATASET_RULE;

                    const response = await callTokenApi(endpoint, method, payload);

                    const isSuccess = response?.data?.code === 200 || response?.data?.code === 201;
                    const finalData = response?.data.data;

                    if (!(isSuccess && finalData)) {
                         toast.error('Failed to save the template. Please try again.');
                         return;
                    }

                    if (!oldData) {
                         const statusUpdatePayload = {
                              accountId: activeAccount.accountId,
                              status: true,
                              lastEditStep: lastEditStep > currentStep ? lastEditStep : currentStep,
                         };
                         const statusUpdateEndpoint = API_AI_INSIGHTS_INTENT_DATASET_BY_ID.replace(':id', stepsData.id);
                         const statusUpdateResponse = await callTokenApi(statusUpdateEndpoint, 'PUT', statusUpdatePayload);
                         if (statusUpdateResponse?.data?.code === 200 || statusUpdateResponse?.data?.code === 201) {
                              finalData.status = true;
                         }
                    }

                    const newStepsData = {
                         ...stepsData,
                         data: finalData.data,
                         oldData: finalData.oldData,
                         lastEditStep: currentStep,
                         status: finalData.status || stepsData.status,
                    };
                    setStepsData(newStepsData);
                   
                    setViewDatasets((prev) => {
                         const itemExists = prev.some((tpl) => tpl.id === datasetId);
                         if (itemExists) {
                              return prev.map((tpl) => (tpl.id === datasetId ? newStepsData : tpl));
                         }
                         return [prev];
                    });

                    toast.success(`Data set rule ${isUpdate ? 'updated' : 'created'} successfully.`);
                    setActiveStep(activeStep);
               } catch (err) {
                    console.error('Submit error:', err);
                    toast.error('Something went wrong. Please try again later.');
               }
          },
          [activeAccount.accountId, activeStep, datasetId, lastEditStep, setActiveStep, setStepsData, setViewDatasets, oldData]
     ); // eslint-disable-line react-hooks/exhaustive-deps

     const handleEditDataSetValues = useCallback(() => {
          dispatch(
               setFlexibleMultiModal({
                    show: true,
                    component: COMPONENT_NAME.AI_INTENT_DATASET_VALUES,
                    attributes: {
                         ruleId: datasetId,
                         setViewDatasets,
                         stepsData,
                         setStepsData,
                         onAccept: onSubmit,
                         handleHasChange
                    },
               })
          );
     }, [dispatch, datasetId, setViewDatasets, stepsData, setStepsData, onSubmit, handleHasChange]);

     useEffect(() => {
          if (!stepsData.oldData && activeStep === 2) {
               handleEditDataSetValues();
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [activeStep === 2]);

     return (
          <StepCardHeader
               title={`Build and Edit Your Data Set`}
               step={2}
               activeStep={activeStep}
               lastEditStep={stepsData.lastEditStep}
               setActiveStep={() => setActiveStep(2)}
          >
               <p>
                    Use the lookup table to manually manage your data set. Updates and changes to your classification only apply to future intent
                    analyses, they are not re-applied to analyses already completed.
               </p>

               <div className="accordion list-custom-reports mb-4">
                    <div className="accordion-item border-0">
                         <div className="list-rules">
                              <div className="row-rule d-flex justify-content-between">
                                   <div className="info-wrap d-flex justify-content-between align-items-center py-2">
                                        <div className="info-title">
                                             <h4>{name}</h4>
                                             {description && (
                                                  <div className="d-inline-flex tooltip-wrapper">
                                                       <HoverInfoIconTooltip>
                                                            <p>{description}</p>
                                                       </HoverInfoIconTooltip>
                                                  </div>
                                             )}
                                        </div>
                                   </div>

                                   <div className="options-wrapper d-flex">
                                        <div className="option-item hover-show-tooltip">
                                             <CLink className="option text-primary" onClick={handleEditDataSetValues}>
                                                  <CIcon icon="icon-pencil" height={14} />
                                             </CLink>
                                             <div className="block-tooltip">
                                                  <CIcon icon="arrowTooltip" className="arrow-tooltip-preview" />
                                                  <div className="block-tooltip-text">
                                                       <p>Edit Data Set values</p>
                                                  </div>
                                             </div>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>

          </StepCardHeader>
     );
};

export default BuildEditDataSet;
